import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Square, Shield, Clock, Phone, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { ModernLogo } from "@/components/ModernLogo";
import tilerImage from "@/assets/real-tiler.jpg";

export const TilingPage = () => {
  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SEO
        title="Professional Tiling Services - Expert Tilers | Sortable"
        description="Find qualified tilers near you. Bathroom tiling, kitchen tiles, floor tiling, and wall tiling services. Fully insured tiling specialists across the UK."
        keywords="tiler, tiling services, bathroom tiling, kitchen tiling, floor tiling, wall tiling, ceramic tiles, porcelain tiles, natural stone, mosaic tiling, tile repair, tile replacement, waterproofing, grouting, tile cutting, bathroom renovation, kitchen backsplash, wet room tiling, commercial tiling, residential tiling, local tiler"
        canonical="https://sortable.lovable.app/services/tiling"
        ogImage="https://sortable.lovable.app/real-tiler.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Professional Tiling Services",
          "description": "Professional tiling services for bathrooms, kitchens and floors",
          "provider": {
            "@type": "Organization",
            "name": "Sortable"
          },
          "serviceType": "Tiling Services",
          "areaServed": {
            "@type": "Country",
            "name": "United Kingdom"
          }
        }}
      />

      {/* Header */}
      <header className="bg-background/95 backdrop-blur-sm border-b border-border/50 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-2 md:py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <ModernLogo showTagline={false} />
            </Link>
            <div className="flex items-center gap-1 md:gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <span className="hidden md:inline">Home</span>
                  <span className="md:hidden">Home</span>
                </Button>
              </Link>
              <Link to="/for-tradesmen">
                <Button variant="outline" size="sm">
                  <span className="hidden md:inline">For Tradesmen</span>
                  <span className="md:hidden">Tradesmen</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-600/10 border border-emerald-600/20 text-emerald-600 px-6 py-3 rounded-full text-lg font-semibold mb-8 shadow-elegant">
            <Square className="h-5 w-5" />
            Professional Tiling Services
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-hero bg-clip-text text-transparent">
            Expert Tilers & Tiling Specialists
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            Transform your bathrooms, kitchens, and floors with professional tiling services. From ceramic to natural stone, our skilled tilers deliver precision workmanship.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/create-job">
              <Button size="lg" className="bg-gradient-hero hover:shadow-glow transform hover:scale-105">
                <Users className="mr-2 h-5 w-5" />
                Find Tilers Now
              </Button>
            </Link>
            <Link to="/for-tradesmen">
              <Button size="lg" variant="outline">
                <Square className="mr-2 h-5 w-5" />
                Join as a Tiler
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 px-4 bg-background/50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Complete Tiling Services
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our network of qualified tilers provides comprehensive tiling solutions for residential and commercial properties.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Bathroom Tiling",
                description: "Complete bathroom tiling including walls, floors, and shower areas",
                icon: <Square className="w-6 h-6" />
              },
              {
                title: "Kitchen Tiling",
                description: "Kitchen backsplashes, worktop areas, and floor tiling solutions",
                icon: <CheckCircle className="w-6 h-6" />
              },
              {
                title: "Floor Tiling",
                description: "Ceramic, porcelain, and natural stone floor installation",
                icon: <Shield className="w-6 h-6" />
              },
              {
                title: "Wall Tiling",
                description: "Decorative and functional wall tiling for all areas of your home",
                icon: <Square className="w-6 h-6" />
              },
              {
                title: "Tile Repair & Replacement",
                description: "Professional tile repair, replacement, and re-grouting services",
                icon: <Clock className="w-6 h-6" />
              },
              {
                title: "Wet Room Installation",
                description: "Waterproof wet room tiling with proper drainage and sealing",
                icon: <Shield className="w-6 h-6" />
              }
            ].map((service, index) => (
              <Card key={index} className="border-border/50 hover:shadow-elegant transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-600/10 rounded-lg text-emerald-600">
                      {service.icon}
                    </div>
                    <CardTitle className="text-lg">{service.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose Our Tiling Network?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              All our tilers are skilled craftsmen specializing in precision installation and waterproofing.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Precision Installation</h4>
                    <p className="text-muted-foreground">
                      Expert tile cutting, alignment, and installation for perfect finishes every time.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Waterproof Solutions</h4>
                    <p className="text-muted-foreground">
                      Professional waterproofing and sealing for bathrooms and wet areas.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Premium Materials</h4>
                    <p className="text-muted-foreground">
                      High-quality tiles, adhesives, and grouting materials for lasting durability.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Design Consultation</h4>
                    <p className="text-muted-foreground">
                      Expert advice on tile selection, patterns, and design for optimal results.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src={tilerImage} 
                alt="Professional tiler working on bathroom installation"
                className="rounded-lg shadow-lg w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Need a Tiler Today?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Post your tiling project and get quotes from qualified, local tilers. Professional installation and waterproofing guaranteed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/create-job">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="bg-white text-primary hover:bg-gray-100 font-semibold px-8 py-3 border-white"
                >
                  <Users className="mr-2 h-5 w-5" />
                  Get Quotes Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-background border-t py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <ModernLogo showTagline={false} />
              <p className="text-muted-foreground mt-4 max-w-md">
                Connecting you with qualified tilers across the UK for all your tiling needs.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li>Bathroom Tiling</li>
                <li>Kitchen Tiling</li>
                <li>Floor Tiling</li>
                <li>Tile Repair</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Customers</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/" className="hover:text-primary">Home</Link></li>
                <li><Link to="/how-it-works-customer" className="hover:text-primary">How It Works</Link></li>
                <li><Link to="/create-job" className="hover:text-primary">Post a Job</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Tilers</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/for-tradesmen" className="hover:text-primary">Join Our Network</Link></li>
                <li><Link to="/tradesman-registration" className="hover:text-primary">Register Now</Link></li>
                <li><Link to="/how-it-works" className="hover:text-primary">How It Works</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};